import React from 'react';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { useFeature } from 'flagged';
import OpportunityClosedState from '@/constants/OpportunityClosedState';
import FeatureFlags from '@/constants/system/FeatureFlags';
import useOpportunity from '@/hooks/swr/useOpportunity';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import { useEntityDetails } from '@/modules/EntityDetails';
import { formatCurrency } from '@/utils/currency';
import { formatDate } from '@/utils/dates';
import useOpportunityServices from '../hooks/useOpportunityServices';
import OpportunityContributionItem from './OpportunityContributionItem';

function OpportunityContributionSummary({
  opportunityId,
}: {
  opportunityId: string;
}) {
  const hasLinking = useFeature(FeatureFlags.OPPORTUNITIES_LINKING);

  const { openDialog, closeDialog } = useDialogManager();
  const { data: opportunity } = useOpportunity(opportunityId);
  const { openDonationEntity } = useEntityDetails();
  const handleLink = React.useCallback(
    () =>
      openDialog(Dialogs.LINK_TO_OPPORTUNITY, {
        opportunityId,
        onClose: closeDialog,
      }),
    [opportunityId],
  );
  const { updateOpportunity } = useOpportunityServices();
  const handleUnlink = React.useCallback(
    (contributionId: string) => {
      if (!opportunity) return;
      const contributionIds = (opportunity.contributionIds || []).filter(
        (id) => id !== contributionId,
      );
      updateOpportunity(opportunityId, { contributionIds });
    },
    [opportunity, updateOpportunity],
  );

  const handleView = React.useCallback(
    (contributionId: string) => {
      if (!opportunity?.constituentId) return;
      openDonationEntity(opportunity.constituentId, contributionId);
    },
    [opportunity],
  );

  if (!opportunity) return null;

  const isWon = opportunity.closedState === OpportunityClosedState.WON;
  const isClosed = Boolean(opportunity.closedState);

  return (
    <div
      className="rounded-lg bg-grey-100 p-4"
      data-testid="opportunity-contribution-summary"
    >
      <h3 className="mb-3 flex items-baseline type-body2">
        <span>
          {translate(
            isClosed
              ? 'OPPORTUNITIES_ACTUAL_LABEL'
              : 'OPPORTUNITIES_PROJECTED_LABEL',
          )}{' '}
        </span>
        {hasLinking && isWon && (
          <Button className="ml-auto p-0" onClick={handleLink}>
            {translate('OPPORTUNITIES_LINK_CONTRIBUTION')}
          </Button>
        )}
      </h3>
      <div className="text-3xl">
        {formatCurrency(
          isClosed ? opportunity.actualValue : opportunity.factoredAmount,
          true,
        )}
      </div>
      {!!opportunity.updated && (
        <div className="mt-3 type-caption1">
          {translate('DONATION_LAST_UPDATED', {
            date: formatDate(opportunity.updated),
          })}
        </div>
      )}
      {hasLinking && !!opportunity.contributionIds?.length && (
        <>
          <Divider className="my-4" />
          <h3 className="text-body4 mb-2 font-medium">
            {translate('CONTRIBUTION_LINKED_CONTRIBUTIONS')}
          </h3>
          <table className="w-full">
            <tbody>
              {opportunity.contributionIds.map((id) => (
                <OpportunityContributionItem
                  contributionId={id}
                  key={id}
                  onUnlink={handleUnlink}
                  onView={handleView}
                />
              ))}
              <tr className="grid grid-cols-[1fr_1fr_100px] items-center pt-1 type-body2">
                <td>{formatCurrency(opportunity.receivedAmount, true)}</td>
                <td>{translate('REPORTS_TOTAL_RECEIVED')}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export default OpportunityContributionSummary;
